// GroupChess Content Model
// A SaaS-style credit-based group chess lessons platform

export const navLinks = [
  { label: "Admin", href: "/admin" },  // Will be replaced with /coach/:username/admin by GroupChessLayout
  { label: "Dashboard", href: "/dashboard", isButton: true },  // Will be replaced with /coach/:username/dashboard by GroupChessLayout
];

export const hero = {
  title: "Learn Chess With Me",
  subtitle: "Interactive group lessons for players ready to improve. We'll cover all types of topics, game reviews, and more.",
  ctaPrimary: "View Pricing",
  ctaSecondary: "See Available Classes",
};

export const howItWorks = [
  {
    step: 1,
    title: "Buy Credits",
    description: "Purchase a credit pack that fits your learning goals. No subscriptions, no commitments.",
    icon: "CreditCard",
  },
  {
    step: 2,
    title: "Browse Classes",
    description: "Explore our schedule of live group lessons across all skill levels.",
    icon: "Calendar",
  },
  {
    step: 3,
    title: "Join & Learn",
    description: "Use your credits to book classes and join live sessions with other students.",
    icon: "Users",
  },
];

export const creditPacks = [
  {
    name: "Starter",
    credits: 5,
    price: 29,
    perCredit: 5.80,
    popular: false,
    description: "Perfect for trying out the platform",
  },
  {
    name: "Standard",
    credits: 10,
    price: 49,
    perCredit: 4.90,
    popular: true,
    description: "Our most popular choice",
  },
  {
    name: "Premium",
    credits: 20,
    price: 79,
    perCredit: 3.95,
    popular: false,
    description: "Best value for dedicated learners",
  },
];

export const classPreview = [
  {
    id: "class-1",
    title: "Opening Fundamentals",
    instructor: "GM Sarah Chen",
    level: "Beginner",
    duration: "60 min",
    description: "Master the key principles of chess openings. Learn how to control the center, develop your pieces efficiently, and castle safely.",
    creditCost: 1,
  },
  {
    id: "class-2",
    title: "Tactical Patterns",
    instructor: "IM Michael Ross",
    level: "Intermediate",
    description: "Discover common tactical motifs like pins, forks, skewers, and discovered attacks. Practice spotting combinations in real game positions.",
    duration: "75 min",
    creditCost: 2,
  },
  {
    id: "class-3",
    title: "Endgame Mastery",
    instructor: "FM Elena Volkov",
    level: "Intermediate",
    description: "Essential endgame techniques including king and pawn endings, rook endgames, and the principles of piece coordination.",
    duration: "60 min",
    creditCost: 1,
  },
  {
    id: "class-4",
    title: "Positional Strategy",
    instructor: "GM David Park",
    level: "Advanced",
    description: "Deep dive into strategic concepts: weak squares, pawn structures, piece placement, and long-term planning.",
    duration: "90 min",
    creditCost: 2,
  },
  {
    id: "class-5",
    title: "Attack & Defense",
    instructor: "IM Sarah Chen",
    level: "Intermediate",
    description: "Learn when to attack and how to defend. Covers king safety, attacking patterns, and defensive resources.",
    duration: "60 min",
    creditCost: 1,
  },
  {
    id: "class-6",
    title: "Game Analysis Workshop",
    instructor: "GM David Park",
    level: "All Levels",
    description: "Bring your games for review! Learn from your mistakes and discover hidden opportunities in your play.",
    duration: "75 min",
    creditCost: 2,
  },
];

export const testimonials = [
  {
    quote: "The group format is fantastic. You learn from other students' questions and mistakes, not just your own. My rating jumped 200 points in 3 months!",
    author: "Alex Thompson",
    role: "Club Player, 1400 → 1600",
    avatar: "A",
  },
  {
    quote: "I was skeptical about online group lessons, but the instructors make it feel personal. The credit system is flexible and the classes are always engaging.",
    author: "Maria Santos",
    role: "Adult Beginner",
    avatar: "M",
  },
  {
    quote: "Perfect for my busy schedule. I can buy credits when I have time and book classes that fit my week. The instructors are world-class.",
    author: "James Wilson",
    role: "Working Professional",
    avatar: "J",
  },
];

export const faq = [
  {
    question: "What's the difference between group and private lessons?",
    answer: "Group lessons are shared sessions where multiple students learn together with one coach — great for collaborative learning and hearing different perspectives. Private lessons are 1-on-1 time with the coach, fully tailored to your level and goals. You pick a time slot that works for you from the coach's availability.",
  },
  {
    question: "How do credits work?",
    answer: "Credits are used for booking group lessons. Purchase a credit pack from your coach's page, then use 1 credit to register for any of their upcoming group lessons. Credits are valid for 6 months from the date of purchase. Private lessons are booked separately with direct payment — no credits needed.",
  },
  {
    question: "How do I buy credits or book a private lesson?",
    answer: "For group lessons, select a credit pack on your coach's page under the pricing section. You'll be taken to a secure checkout, and credits are added to your account instantly. For private lessons, browse the coach's available time slots and pay directly at checkout when booking.",
  },
  {
    question: "What is the cancellation & refund policy?",
    answer: "For group lessons: cancel more than 4 hours before the start time and your credit is returned. For private lessons: cancel more than 4 hours before the start time and you receive a full monetary refund. In both cases, cancellations within 4 hours of the lesson or no-shows are not eligible for a refund. See the full policy in our Coaching Terms & Conditions.",
  },
  {
    question: "What happens if the coach cancels a lesson?",
    answer: "If a coach cancels a group lesson, all registered students are automatically refunded 1 credit each — no action needed on your end. If a coach cancels a private lesson, a full monetary refund is processed automatically.",
  },
  {
    question: "What do I need to join a class?",
    answer: "All live lessons take place via video call directly on the coach's dashboard. You just need a stable internet connection and a device with a camera and microphone. No additional software or downloads required.",
  },
  {
    question: "How are group and private lessons conducted online?",
    answer: "The coach screenshares their chess analysis environment — typically Lichess Studies or Chess.com Analysis. Students follow along in the video call, ask questions, and interact in real time. The format is the same for both group and private lessons.",
  },
  {
    question: "Will I get reminders before a lesson?",
    answer: "Yes. You'll receive an email confirmation when you register for a lesson, and a reminder approximately 1 hour before the lesson starts.",
  },
];

// Dashboard Mock Data
export const dashboardData = {
  user: {
    name: "Chris Poloniato",
    email: "chris@example.com",
    credits: 7,
  },
  joinedLessons: [
    {
      id: "lesson-1",
      title: "Opening Fundamentals",
      instructor: "GM Sarah Chen",
      level: "Beginner",
      datetime: "Mon, Jan 27 • 7:00 PM",
      zoomLink: "https://zoom.us/j/123456789",
      status: "upcoming",
    },
    {
      id: "lesson-2",
      title: "Tactical Patterns",
      instructor: "IM Michael Ross",
      level: "Intermediate",
      datetime: "Wed, Jan 29 • 8:00 PM",
      zoomLink: "https://zoom.us/j/987654321",
      status: "upcoming",
    },
    {
      id: "lesson-3",
      title: "Endgame Basics",
      instructor: "FM Elena Volkov",
      level: "Beginner",
      datetime: "Fri, Jan 17 • 6:00 PM",
      zoomLink: null,
      status: "completed",
      recording: "https://example.com/recording/123",
    },
  ],
  upcomingSchedule: [
    {
      id: "sched-1",
      title: "Opening Fundamentals",
      instructor: "GM Sarah Chen",
      level: "Beginner",
      datetime: "Mon, Jan 27 • 7:00 PM",
      spotsLeft: 4,
      creditCost: 1,
    },
    {
      id: "sched-2",
      title: "Tactical Patterns",
      instructor: "IM Michael Ross",
      level: "Intermediate",
      datetime: "Wed, Jan 29 • 8:00 PM",
      spotsLeft: 2,
      creditCost: 2,
    },
    {
      id: "sched-3",
      title: "Positional Strategy",
      instructor: "GM David Park",
      level: "Advanced",
      datetime: "Thu, Jan 30 • 7:30 PM",
      spotsLeft: 8,
      creditCost: 2,
    },
    {
      id: "sched-4",
      title: "Endgame Mastery",
      instructor: "FM Elena Volkov",
      level: "Intermediate",
      datetime: "Fri, Jan 31 • 6:00 PM",
      spotsLeft: 6,
      creditCost: 1,
    },
    {
      id: "sched-5",
      title: "Attack & Defense",
      instructor: "IM Sarah Chen",
      level: "Intermediate",
      datetime: "Sat, Feb 1 • 10:00 AM",
      spotsLeft: 10,
      creditCost: 1,
    },
    {
      id: "sched-6",
      title: "Game Analysis Workshop",
      instructor: "GM David Park",
      level: "All Levels",
      datetime: "Sun, Feb 2 • 3:00 PM",
      spotsLeft: 3,
      creditCost: 2,
    },
  ],
};

export const supportFaq = [
  {
    question: "How do I buy credits?",
    answer: "Credits are for group lessons only. From your coach's page, select a credit pack under the pricing section. You'll be taken to a secure checkout. Once payment completes, credits are added to your account automatically. You'll see your updated balance in your dashboard. For individual sessions, you pay directly at checkout when booking a time slot — no credits needed.",
  },
  {
    question: "How do I register for a lesson?",
    answer: "For group lessons, go to the \"Schedule\" tab in your dashboard to browse available upcoming lessons. Click \"Book,\" confirm in the popup, and 1 credit will be deducted. The lesson will appear in your \"My Lessons\" tab. For individual sessions, browse available time slots on the coach's page and complete payment at checkout.",
  },
  {
    question: "How do I join a live lesson?",
    answer: "A \"Join\" button appears on your lesson card 20 minutes before the scheduled start time. Click it to enter the video room directly in your browser — no downloads needed. Look for the \"Lesson is Live\" banner at the top of your dashboard when a session is active.",
  },
  {
    question: "How do I cancel a booking?",
    answer: "In \"My Lessons,\" click \"Cancel Booking\" on the lesson. For group lessons, your credit is returned immediately as long as the lesson is more than 4 hours away. For individual sessions, a full monetary refund is processed as long as the session is more than 4 hours away. Cancellations within 4 hours are not eligible for a refund.",
  },
  {
    question: "How do I check my credit balance?",
    answer: "Your current credit balance is displayed at the top of your dashboard. Credits expire 6 months after purchase — your balance always reflects your available, non-expired credits. Note: credits only apply to group lessons. Individual session payments are separate and do not use credits.",
  },
];

// Carousel Lessons for Landing Page
export const carouselLessons = [
  {
    id: "carousel-1",
    level: "Intermediate",
    title: "Intermediate Chess Lesson",
    instructor: "with TheChessNerd",
    date: {
      display: "Saturday, January 31",
      times: [
        { tz: "SAST", time: "4:30 PM" },
        { tz: "CET", time: "3:30 PM" },
        { tz: "EST", time: "9:30 AM" },
      ],
    },
    duration: 60,
    priceUSD: 15,
    priceZAR: 248,
    spotsTotal: 10,
    spotsAvailable: 10,
  },
  {
    id: "carousel-2",
    level: "Beginner",
    title: "Beginner Fundamentals",
    instructor: "with GM Sarah Chen",
    date: {
      display: "Sunday, February 2",
      times: [
        { tz: "SAST", time: "3:00 PM" },
        { tz: "CET", time: "2:00 PM" },
        { tz: "EST", time: "8:00 AM" },
      ],
    },
    duration: 60,
    priceUSD: 12,
    priceZAR: 199,
    spotsTotal: 12,
    spotsAvailable: 8,
  },
  {
    id: "carousel-3",
    level: "Advanced",
    title: "Advanced Tactics Workshop",
    instructor: "with GM David Park",
    date: {
      display: "Monday, February 3",
      times: [
        { tz: "SAST", time: "6:00 PM" },
        { tz: "CET", time: "5:00 PM" },
        { tz: "EST", time: "11:00 AM" },
      ],
    },
    duration: 90,
    priceUSD: 20,
    priceZAR: 329,
    spotsTotal: 8,
    spotsAvailable: 3,
  },
  {
    id: "carousel-4",
    level: "Intermediate",
    title: "Endgame Mastery",
    instructor: "with FM Elena Volkov",
    date: {
      display: "Wednesday, February 5",
      times: [
        { tz: "SAST", time: "5:00 PM" },
        { tz: "CET", time: "4:00 PM" },
        { tz: "EST", time: "10:00 AM" },
      ],
    },
    duration: 60,
    priceUSD: 15,
    priceZAR: 248,
    spotsTotal: 10,
    spotsAvailable: 6,
  },
  {
    id: "carousel-5",
    level: "Beginner",
    title: "Opening Repertoire Builder",
    instructor: "with IM Michael Ross",
    date: {
      display: "Saturday, February 8",
      times: [
        { tz: "SAST", time: "4:00 PM" },
        { tz: "CET", time: "3:00 PM" },
        { tz: "EST", time: "9:00 AM" },
      ],
    },
    duration: 75,
    priceUSD: 15,
    priceZAR: 248,
    spotsTotal: 12,
    spotsAvailable: 12,
  },
];
